import {Injectable} from '@angular/core';
import {EventService} from './event.service';
import {tap, withLatestFrom} from 'rxjs/operators';
import {UIMenu, votumBlocksWorkflow} from '../models/uiArtefakt';
import {getAllMenuStateSeiten, MenuGruppe, MenuSeite, MenuState} from '../models/menuState';
import {combineLatest, Subscription} from 'rxjs';
import {ErrorService, StringUtils} from '@lv1871/core';
import {ShellConfigService} from './shell-config.service';

@Injectable()
export class MenuStateService {
  private _subscription: Subscription = Subscription.EMPTY;
  private _seitenIndexBeiWorkflowStopBeiVotum: number = 0;

  public constructor(private _eventService: EventService, private _shellConfigService: ShellConfigService,
                     private _errorService: ErrorService) {
    this.init();
  }

  public init() {
    this._subscription.unsubscribe();
    this._subscription = combineLatest([this._eventService.menuItemSelected$, this._eventService.validationState$]).pipe(
        withLatestFrom(this._eventService.menu$, this._eventService.uiControls$),
        tap(([[menuItemSelected, validationMap], menu, uiControls]) => {
          const workflowStopBeiVotum = this._shellConfigService.getParameter().uiWorkflowStopBeiVotum;
          const menuState = new MenuState();
          this._setAttributes(menu, menuState, validationMap);
          const selectedIndex = this._setSelectedState(menuState, menuItemSelected);
          this._setEnabledState(menuState, votumBlocksWorkflow(uiControls), workflowStopBeiVotum, selectedIndex);
          this._validateSeitenIds(menuState);
          this._eventService.menuState$.next(menuState);
        })
    ).subscribe();
  }

  private _setAttributes(menu: UIMenu, menuState: MenuState, validationMap: Map<string, boolean>) {
    let seitenIndex = 0;
    menu.UIMenuGruppen.forEach(menugruppe => {
      const menuStateGruppe = new MenuGruppe();
      menuStateGruppe.Identifikator = menugruppe.Identifikator;
      menuStateGruppe.Titel = menugruppe.MenuTitel;
      menuState.MenuGruppen.push(menuStateGruppe);
      menugruppe.UIMenuSeiten.forEach(menuseite => {
        const menuStateSeite = new MenuSeite();
        menuStateSeite.Identifikator = menuseite.Identifikator;
        menuStateSeite.SeitenId = menuseite.SeitenID;
        menuStateSeite.Titel = menuseite.MenuTitel;
        menuStateSeite.valid = validationMap.get(menuseite.Identifikator);
        menuStateSeite.index = seitenIndex++;
        menuStateGruppe.MenuSeiten.push(menuStateSeite);
      });
    });
  }

  // SeitenId muss über alle Seiten eindeutig sein
  private _validateSeitenIds(menuState: MenuState) {
    const alleSeiten: MenuSeite[] = getAllMenuStateSeiten(menuState);

    alleSeiten.forEach((menuseite: MenuSeite, index) => {
      const seitenId = menuseite.SeitenId;
      if (StringUtils.isNotNull(seitenId)) {
        const anzahlSeitenMitIdentischerSeitenId = alleSeiten.filter(seite => seitenId === seite.SeitenId);
        if (anzahlSeitenMitIdentischerSeitenId.length > 1) {
          let seiten = '';
          anzahlSeitenMitIdentischerSeitenId.forEach(seite => {
            seiten = seiten + ' ' + seite.Titel;
          });
          this._errorService.pushErrorMessage(`Es werden mehrere Seiten mit der SeitenId '${seitenId}' verwendet: ${seiten}`);
        }
      }
    });
  }

  // Enabled sind Seiten bis zur ersten Seite die invalid ist, sowie die Seite danach
  private _setEnabledState(menuState: MenuState, votumVorhanden: boolean, workflowStopBeiVotum: boolean,
                           selectedIndex: number) {
    const alleSeiten: MenuSeite[] = getAllMenuStateSeiten(menuState);
    const indexOfFirstInvalidSeite = alleSeiten.findIndex(menuseite => !menuseite.valid);
    if (workflowStopBeiVotum && votumVorhanden) {
      if (!this._seitenIndexBeiWorkflowStopBeiVotum) {
        this._seitenIndexBeiWorkflowStopBeiVotum = selectedIndex;
      }
      alleSeiten.forEach((menuseite: MenuSeite, index) => {
        if (index <= this._seitenIndexBeiWorkflowStopBeiVotum) {
          menuseite.enabled = true;
        } else {
          menuseite.enabled = false;
        }
      });
    } else {
      alleSeiten.forEach((menuseite: MenuSeite, index) => {
        this._seitenIndexBeiWorkflowStopBeiVotum = 0;
        if (index < indexOfFirstInvalidSeite) {
          menuseite.enabled = menuseite.valid;
        } else if (indexOfFirstInvalidSeite < 0 || index === indexOfFirstInvalidSeite ||
            (index === indexOfFirstInvalidSeite + 1 && alleSeiten[indexOfFirstInvalidSeite]?.selected)) {
          menuseite.enabled = true;
        } else {
          menuseite.enabled = false;
        }
      });
    }
  }

  private _setSelectedState(menuState: MenuState, menuItemSelected: string): number {
    let selectedIndex = 0;
    const menuSeiten = getAllMenuStateSeiten(menuState);
    menuSeiten.filter((seite, index) => {
      if (seite.Identifikator === menuItemSelected) {
        selectedIndex = index;
      }
      return seite.Identifikator === menuItemSelected;
    }).forEach(seite => seite.selected = true);
    return selectedIndex;
  }

}
